import { css } from 'aphrodite';
import { Button } from '../../../../../../../web/components/Button';
import { pink2, successTint } from '../../../../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../../../../web/styles/styles';
import { IEventRegistrationSurvey, IEventSurveyResponse } from '../../../../../../models/Api';
import { AddToCalendarDropdown } from '../AddToCalendarDropdown';
import { getEventRecipientFirstAndLastName } from '../utils';
import { styleSheet } from './styles';

type Props = {
	onChangeResponseClick: () => void;
	surveyResponse: IEventSurveyResponse;
	survey: IEventRegistrationSurvey;
	token: string;
};

export const EventSurveyConfirmation = ({ onChangeResponseClick, surveyResponse, survey, token }: Props) => {
	return (
		<div className={css(styleSheet.container)}>
			<EventResponseMessage surveyResponse={surveyResponse} survey={survey} />
			<div className={css(styleSheet.divider)} />
			<div className={css(styleSheet.buttonGroup)}>
				{surveyResponse.isAttending && (
					<AddToCalendarDropdown survey={survey} surveyResponseId={surveyResponse.id} token={token} />
				)}
				<Button label='Change Response' onClick={onChangeResponseClick} />
			</div>
		</div>
	);
};

const EventResponseMessage = ({
	surveyResponse,
	survey,
}: {
	surveyResponse: IEventSurveyResponse;
	survey: IEventRegistrationSurvey;
}) => {
	const hasNamedGuests = !!surveyResponse.guests?.length;
	const anonymousGuests = surveyResponse.anonymousGuestCount;
	const hasTieredTickets = !!survey.attendeeOptions?.eventTickets?.length;

	const addAdditionalAnonymousGuestCount = () => {
		if (anonymousGuests) {
			return `and ${anonymousGuests} other guest${anonymousGuests === 1 ? '' : 's'}`;
		}
		return '';
	};

	const getGuestContent = () => {
		if (hasNamedGuests) {
			return (
				<>
					<span>with the following guests:&nbsp;</span>
					{surveyResponse.guests.map((guest, index) => {
						const { firstName, lastName } = getEventRecipientFirstAndLastName(guest);
						return (
							<strong
								key={`${firstName}-${lastName}`}
								className={css(baseStyleSheet.fontBold)}
							>{`${firstName} ${lastName}${index !== surveyResponse.guests.length - 1 ? ',' : ''} `}</strong>
						);
					})}
					{addAdditionalAnonymousGuestCount()}
				</>
			);
		} else if (anonymousGuests) {
			return anonymousGuests > 1 ? <span>with {anonymousGuests} guests</span> : <span>with a guest</span>;
		}
	};

	const getTicketHoldersSection = () => {
		if (!hasTieredTickets || !hasNamedGuests) {
			return null;
		}

		const attendee = surveyResponse.attendee;
		const guests = surveyResponse.guests || [];
		const totalTickets = (attendee ? 1 : 0) + guests.length;

		// Calculate ticket breakdown by type
		const ticketCounts: Record<string, { count: number; name: string; price?: number }> = {};

		// Count attendee ticket
		if (attendee?.ticket?.id) {
			const ticketId = attendee.ticket.id;
			if (!ticketCounts[ticketId]) {
				ticketCounts[ticketId] = {
					count: 0,
					name: attendee.ticket.name || 'Ticket',
					price: attendee.ticket.unitPrice,
				};
			}
			ticketCounts[ticketId].count += 1;
		}

		// Count guest tickets
		guests.forEach(guest => {
			if (guest.ticket?.id) {
				const ticketId = guest.ticket.id;
				if (!ticketCounts[ticketId]) {
					ticketCounts[ticketId] = {
						count: 0,
						name: guest.ticket.name || 'Ticket',
						price: guest.ticket.unitPrice,
					};
				}
				ticketCounts[ticketId].count += 1;
			}
		});

		return (
			<div className={css(styleSheet.ticketSection)}>
				{/* Ticket holders list */}
				<h5 className={css(styleSheet.ticketHolderListTitle)}>Ticket Holders: ({totalTickets})</h5>
				<ul className={css(styleSheet.ticketHolderList)}>
					{attendee && (
						<li className={css(styleSheet.ticketHolder)}>
							<span className={css(baseStyleSheet.fontBold)}>
								{attendee.firstName} {attendee.lastName}
							</span>
							{attendee.ticket?.name && <span className={css(styleSheet.ticketType)}> ({attendee.ticket.name})</span>}
							{attendee.email && <span className={css(styleSheet.ticketHolderEmail)}> - {attendee.email}</span>}
						</li>
					)}
					{guests.map((guest, index) => {
						const { firstName, lastName } = getEventRecipientFirstAndLastName(guest);
						return (
							<li key={`ticket-holder-${index}`} className={css(styleSheet.ticketHolder)}>
								<strong>
									{firstName} {lastName}
								</strong>
								{guest.ticket?.name && <span className={css(styleSheet.ticketType)}> ({guest.ticket.name})</span>}
								{guest.email && <span className={css(styleSheet.ticketHolderEmail)}> - {guest.email}</span>}
							</li>
						);
					})}
				</ul>
			</div>
		);
	};

	return (
		<div
			className={css(styleSheet.eventResponseMessage)}
			style={{ background: surveyResponse.isAttending ? successTint : pink2 }}
		>
			<div>
				<p>
					You have marked that you&nbsp;
					<strong className={css(baseStyleSheet.fontBold)}>
						{surveyResponse.isAttending ? 'are attending' : 'are not attending'}
					</strong>
					&nbsp;this event&nbsp;
					{!hasTieredTickets && getGuestContent()}
				</p>
				{surveyResponse.isAttending && survey?.isPaidEvent && !hasTieredTickets ? (
					<p>Please check your email for your tickets and event details</p>
				) : null}
				{surveyResponse.isAttending && hasTieredTickets && (
					<>
						{getTicketHoldersSection()}
						<p className={css(styleSheet.emailNote)}>Please check your email for your tickets and event details</p>
					</>
				)}
			</div>
		</div>
	);
};
